/** Media text from the Store API is `custom_title`; older integrations may use alt/altText. */
type ImageMetadata = Record<string, unknown>;

function asImage(value: unknown): ImageMetadata | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as ImageMetadata)
    : undefined;
}

function altText(value: unknown): string {
  if (typeof value !== 'string') return '';
  const text = value.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim();
  // An image address is not a text alternative. Do not stringify malformed API objects.
  if (/^(?:https?:\/\/|\/|data:|blob:)/i.test(text)) return '';
  return text;
}

/**
 * Resolve an informative image's ALT, not decorative images (keep their explicit alt="").
 * Context labels precede the filename fallback; metadata always belongs to the displayed image.
 * This module intentionally has no React, browser, locale, or server dependencies.
 */
export function resolveImageAlt(image: unknown, ...fallbacks: unknown[]): string {
  const media = asImage(image);
  const cameraFilename = /^(?:DSC|IMG|PXL)[ _-]?\d+(?:[ _-]+(?:Enhanced[ _-]+NR|edited))?(?:\.(?:jpe?g|png|webp))?$/i;
  const context = fallbacks.map(altText).find(text => text && !cameraFilename.test(text));
  for (const candidate of [media?.custom_title, media?.alt, media?.altText, ...fallbacks, media?.name]) {
    const text = altText(candidate);
    // Camera filenames reused by different products do not identify an image link's destination.
    // Keep authored descriptions, and keep the old filename fallback when no context exists.
    if (context && cameraFilename.test(text)) continue;
    if (text) return text;
  }
  return '';
}

export function imageSource(image: unknown): string | undefined {
  const url = asImage(image)?.url;
  return typeof url === 'string' && url.trim() ? url.trim() : undefined;
}

/** Image-valued attributes store a URL in `name`; use a real label, then the attribute/choice ID. */
export function resolveImageChoiceAlt(value: unknown, attribute: unknown): string {
  const choice = asImage(value);
  const attributeName = altText(asImage(attribute)?.name);
  const id = choice?.id;
  const label = [attributeName, typeof id === 'string' || typeof id === 'number' ? String(id) : '']
    .filter(Boolean).join(' – ');
  return resolveImageAlt(value, choice?.name, label);
}

/** Malformed/null entries must not crash a gallery or create slides with an invalid source. */
export function toLightboxSlides(images: unknown, fallback?: unknown) {
  if (!Array.isArray(images)) return [];
  return images.flatMap(image => {
    const src = imageSource(image);
    if (!src) return [];
    const blurhash = asImage(image)?.blurhash;
    return [{
      src,
      alt: resolveImageAlt(image, fallback),
      blurDataURL: typeof blurhash === 'string' && blurhash.trim() ? blurhash : undefined,
    }];
  });
}

/** Keep variant URL and ALT together, and tolerate incomplete API/variant data. */
export function resolveProductImage(settings: unknown, product: unknown, variants: unknown) {
  const data = asImage(product);
  const category = asImage(asImage(settings)?.category);
  const swapVariant = asImage(category?.swap_variant_image)?.enabled === true;
  const firstVariant = Array.isArray(variants) ? asImage(variants[0]) : undefined;
  const variantImage = Array.isArray(firstVariant?.images) ? firstVariant.images[0] : undefined;
  const image = swapVariant && imageSource(variantImage) ? variantImage : data?.image;
  return { url: imageSource(image), alt: resolveImageAlt(image, data?.name) };
}
